import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Building2, TrendingUp, KeyRound, ShieldCheck, Zap, Wifi } from "lucide-react";
import api from "@/lib/api";
import { MaskedLines, FadeUp, Eyebrow } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { PropertyCard } from "@/components/PropertyCard";
import { Seo } from "@/components/Seo";
import { ORG_JSONLD } from "@/lib/seo";
import { formatDate } from "@/lib/format";

const HERO_IMG = "/api/files/sbs-properties/migrated/10226873-b02f-445b-9b72-13763f7450dc.webp";
const MARKITI_IMG = "/api/files/sbs-properties/migrated/10226873-b02f-445b-9b72-13763f7450dc.webp";

const SERVICES = [
  { icon: Building2, title: "Property Management", text: "Comprehensive management solutions for landlords, ensuring maximum ROI and hassle-free maintenance of your assets." },
  { icon: TrendingUp, title: "Strategic Sales", text: "Expert market analysis and aggressive marketing to help you sell your commercial or residential property at peak value." },
  { icon: KeyRound, title: "Premium Rentals", text: "Finding the perfect tenant for your space or the ideal home for your family with thorough vetting and legal support." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [articles, setArticles] = useState([]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/properties", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/news").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#markiti") {
      const t = setTimeout(() => document.getElementById("markiti")?.scrollIntoView({ behavior: "smooth" }), 300);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title="Mombasa Real Estate — Homes, Offices & Commercial Space"
        description="SBS Properties — Mombasa's trusted real estate partner since 1952. Expert property management, strategic sales and premium rentals across the Kenyan coast."
        jsonLd={ORG_JSONLD}
      />
      {/* Kinetic Hero */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden bg-obsidian grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="SBS Markiti Complex, Mombasa" className="h-full w-full object-cover opacity-50 scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/50" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-4 sm:px-8 pt-40 pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Eyebrow light>Est. 1952 — Mombasa</Eyebrow>
          </motion.div>

          <MaskedLines
            data-testid="hero-headline"
            className="mt-6 font-serif text-ivory text-4xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[1.02]"
            lines={["Excellence in", "property,", <em key="i" className="text-brand-gold not-italic font-medium italic">since 1952.</em>]}
            delay={0.35}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-ivory/70 text-base sm:text-lg leading-relaxed"
          >
            Building the future of Mombasa and the East African coast through expert property management, strategic sales, and premium rentals — trusted for over 70 years.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/listings"
              data-testid="hero-explore-listings-btn"
              className="group flex items-center gap-2 bg-brand px-7 py-3.5 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors duration-300"
            >
              Explore Listings
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#markiti"
              data-testid="hero-markiti-btn"
              className="group flex items-center gap-2 border border-ivory/30 px-7 py-3.5 text-sm font-semibold text-ivory hover:border-brand-gold hover:text-brand-gold transition-colors duration-300"
            >
              SBS Markiti Complex
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

        </motion.div>
      </section>

      {/* Featured Properties */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-8 md:py-16">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Featured Properties</h2>
          </div>
          <Link to="/listings" data-testid="featured-browse-all-btn" className="group flex items-center gap-2 text-sm font-semibold text-brand">
            Browse All Listings
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((p, i) => (
            <div key={p.id} className={i === 0 ? "md:col-span-2 lg:col-span-1" : ""}>
              <PropertyCard property={p} index={i} />
            </div>
          ))}
          {featured.length === 0 && (
            <p className="text-muted-foreground col-span-full" data-testid="featured-empty">Loading featured properties…</p>
          )}
        </div>
      </section>

      {/* Markiti Spotlight */}
      <section id="markiti" className="mt-16 bg-obsidian text-ivory relative grain overflow-hidden" data-testid="markiti-spotlight">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center relative">
          <FadeUp>
            <Eyebrow light>Flagship Project</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-6xl tracking-tight leading-[1.05]">
              SBS Markiti <span className="italic text-brand-gold">Complex</span>
            </h2>
            <p className="mt-6 text-ivory/70 leading-relaxed max-w-lg">
              Strategically located in the heart of Mombasa, the Markiti Complex offers premium retail and office spaces designed for businesses that demand visibility and excellence.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {[
                [ShieldCheck, "24/7 Security"],
                [Wifi, "Fibre Internet"],
                [Zap, "Backup Power"],
              ].map(([Icon, label]) => (
                <span key={label} className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] text-ivory/80">
                  <Icon className="h-4 w-4 text-brand-gold" /> {label}
                </span>
              ))}
            </div>
            <Link
              to="/listings?status=rent"
              data-testid="markiti-view-details-btn"
              className="group mt-10 inline-flex items-center gap-2 bg-brand px-7 py-3.5 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors duration-300"
            >
              View Available Units
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="relative overflow-hidden">
              <img src={MARKITI_IMG} alt="SBS Markiti Complex" className="w-full aspect-[16/10] object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/15" />
              <div className="absolute bottom-4 left-4 bg-obsidian/85 backdrop-blur px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] text-brand-gold">
                Abdel Nasser Road · Mombasa CBD
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <Marquee />

      {/* Services Trio */}
      <section className="bg-sand border-y hairline">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-28">
          <FadeUp>
            <Eyebrow>What We Do</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Our Professional Services</h2>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.1}>
                <div
                  data-testid={`home-service-${i}`}
                  className="group bg-white border hairline p-8 h-full hover:border-brand/50 hover:shadow-[0_24px_60px_-30px_rgba(194,109,56,0.4)] transition-all duration-500"
                >
                  <s.icon className="h-8 w-8 text-brand" />
                  <h3 className="mt-6 font-serif text-2xl group-hover:text-brand transition-colors duration-300">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* News */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-24 md:pb-32">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Insights</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Latest News</h2>
          </div>
          <Link to="/news" data-testid="home-news-all-btn" className="group flex items-center gap-2 text-sm font-semibold text-brand">
            View All News
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {articles.map((a, i) => (
            <FadeUp key={a.id} delay={i * 0.1}>
              <Link to={`/news/${a.slug}`} data-testid={`home-news-card-${i}`} className="group block">
                <div className="overflow-hidden aspect-[16/9]">
                  <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <span className="mt-5 block text-[11px] font-mono uppercase tracking-[0.2em] text-brand font-bold">{a.category}</span>
                <h3 className="mt-2 font-serif text-xl leading-snug group-hover:text-brand transition-colors duration-300">{a.title}</h3>
                <span className="mt-2 block text-xs text-muted-foreground">{formatDate(a.published_at)} · {a.read_time}</span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
